export const DEFAULT_THEME = {
  primary: '#E11D2E',
  secondary: '#0A0A0A',
  accent: '#FFFFFF',
  background: '#050505',
  foreground: '#FFFFFF',
  heading_font: 'Arial',
  body_font: 'Arial',
  hero_title_size: 1,
  section_title_size: 1,
  body_text_size: 1,
  small_text_size: 1,
  heading_color: '#FFFFFF',
  body_color: 'rgba(255,255,255,0.78)',
  muted_text_color: 'rgba(255,255,255,0.62)',
  button_bg: '#E11D2E',
  button_text: '#FFFFFF',
  button_radius: 1,
  button_text_size: 1,
  form_bg: 'rgba(24,24,27,0.5)',
  form_text: '#FFFFFF',
  form_border: 'rgba(255,255,255,0.1)',
  form_radius: 1,
  form_text_size: 1,
  site_zoom: 1,
  radius_scale: 1,
};

export const THEME_PRESETS = [
  { name: 'Crimson', primary: '#E11D2E', secondary: '#0A0A0A', accent: '#FFFFFF', background: '#050505', foreground: '#FFFFFF' },
  { name: 'Cobalt', primary: '#2563EB', secondary: '#0B1120', accent: '#E2E8F0', background: '#0B1120', foreground: '#F8FAFC' },
  { name: 'Emerald', primary: '#10B981', secondary: '#0F172A', accent: '#E2E8F0', background: '#0F172A', foreground: '#F8FAFC' },
  { name: 'Amber', primary: '#F59E0B', secondary: '#111827', accent: '#F8FAFC', background: '#111827', foreground: '#F8FAFC' },
];

function hexToHsl(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return null;
  const r = parseInt(normalized.slice(0, 2), 16) / 255;
  const g = parseInt(normalized.slice(2, 4), 16) / 255;
  const b = parseInt(normalized.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

export function normalizeThemeSettings(theme) {
  return {
    primary: theme?.primary || DEFAULT_THEME.primary,
    secondary: theme?.secondary || DEFAULT_THEME.secondary,
    accent: theme?.accent || DEFAULT_THEME.accent,
    background: theme?.background || DEFAULT_THEME.background,
    foreground: theme?.foreground || DEFAULT_THEME.foreground,
    heading_font: theme?.heading_font || DEFAULT_THEME.heading_font,
    body_font: theme?.body_font || DEFAULT_THEME.body_font,
    hero_title_size: Number(theme?.hero_title_size) > 0 ? Number(theme.hero_title_size) : DEFAULT_THEME.hero_title_size,
    section_title_size: Number(theme?.section_title_size) > 0 ? Number(theme.section_title_size) : DEFAULT_THEME.section_title_size,
    body_text_size: Number(theme?.body_text_size) > 0 ? Number(theme.body_text_size) : DEFAULT_THEME.body_text_size,
    small_text_size: Number(theme?.small_text_size) > 0 ? Number(theme.small_text_size) : DEFAULT_THEME.small_text_size,
    heading_color: theme?.heading_color || DEFAULT_THEME.heading_color,
    body_color: theme?.body_color || DEFAULT_THEME.body_color,
    muted_text_color: theme?.muted_text_color || DEFAULT_THEME.muted_text_color,
    button_bg: theme?.button_bg || DEFAULT_THEME.button_bg,
    button_text: theme?.button_text || DEFAULT_THEME.button_text,
    button_radius: Number(theme?.button_radius) > 0 ? Number(theme.button_radius) : DEFAULT_THEME.button_radius,
    button_text_size: Number(theme?.button_text_size) > 0 ? Number(theme.button_text_size) : DEFAULT_THEME.button_text_size,
    form_bg: theme?.form_bg || DEFAULT_THEME.form_bg,
    form_text: theme?.form_text || DEFAULT_THEME.form_text,
    form_border: theme?.form_border || DEFAULT_THEME.form_border,
    form_radius: Number(theme?.form_radius) > 0 ? Number(theme.form_radius) : DEFAULT_THEME.form_radius,
    form_text_size: Number(theme?.form_text_size) > 0 ? Number(theme.form_text_size) : DEFAULT_THEME.form_text_size,
    site_zoom: Number(theme?.site_zoom) > 0 ? Number(theme.site_zoom) : DEFAULT_THEME.site_zoom,
    radius_scale: Number(theme?.radius_scale) > 0 ? Number(theme.radius_scale) : DEFAULT_THEME.radius_scale,
  };
}

export function getCachedTheme() {
  try {
    const value = localStorage.getItem('site_theme');
    if (!value) return DEFAULT_THEME;
    return normalizeThemeSettings(JSON.parse(value));
  } catch (_error) {
    return DEFAULT_THEME;
  }
}

export function cacheTheme(theme) {
  try {
    localStorage.setItem('site_theme', JSON.stringify(theme));
  } catch (_error) {
    // Ignore storage failures.
  }
}

export function applySiteTheme(theme) {
  const normalized = normalizeThemeSettings(theme);
  const root = document.documentElement;

  root.style.setProperty('--site-primary', normalized.primary);
  root.style.setProperty('--site-secondary', normalized.secondary);
  root.style.setProperty('--site-accent', normalized.accent);
  root.style.setProperty('--site-background', normalized.background);
  root.style.setProperty('--site-foreground', normalized.foreground);
  root.style.setProperty('--font-heading', normalized.heading_font);
  root.style.setProperty('--font-body', normalized.body_font);
  root.style.setProperty('--hero-title-scale', String(normalized.hero_title_size));
  root.style.setProperty('--section-title-scale', String(normalized.section_title_size));
  root.style.setProperty('--body-text-scale', String(normalized.body_text_size));
  root.style.setProperty('--small-text-scale', String(normalized.small_text_size));
  root.style.setProperty('--site-heading-color', normalized.heading_color);
  root.style.setProperty('--site-body-color', normalized.body_color);
  root.style.setProperty('--site-muted-color', normalized.muted_text_color);
  root.style.setProperty('--site-button-bg', normalized.button_bg);
  root.style.setProperty('--site-button-text', normalized.button_text);
  root.style.setProperty('--site-button-radius', `${normalized.button_radius}rem`);
  root.style.setProperty('--site-button-text-size', `${normalized.button_text_size}rem`);
  root.style.setProperty('--site-form-bg', normalized.form_bg);
  root.style.setProperty('--site-form-text', normalized.form_text);
  root.style.setProperty('--site-form-border', normalized.form_border);
  root.style.setProperty('--site-form-radius', `${normalized.form_radius}rem`);
  root.style.setProperty('--site-form-text-size', `${normalized.form_text_size}rem`);
  root.style.setProperty('--site-zoom', String(normalized.site_zoom));
  root.style.setProperty('--site-radius-scale', String(normalized.radius_scale));
  root.style.setProperty('--radius', `${normalized.radius_scale}rem`);
  root.style.setProperty('--focus-ring', normalized.primary);

  const primaryHsl = hexToHsl(normalized.primary);
  const backgroundHsl = hexToHsl(normalized.background);
  const foregroundHsl = hexToHsl(normalized.foreground);

  if (primaryHsl) {
    root.style.setProperty('--primary', `${primaryHsl.h} ${primaryHsl.s}% ${primaryHsl.l}%`);
    root.style.setProperty('--ring', `${primaryHsl.h} ${primaryHsl.s}% ${primaryHsl.l}%`);
    root.style.setProperty('--chart-1', `${primaryHsl.h} ${primaryHsl.s}% ${primaryHsl.l}%`);
  }
  if (backgroundHsl) {
    root.style.setProperty('--background', `${backgroundHsl.h} ${backgroundHsl.s}% ${backgroundHsl.l}%`);
    root.style.setProperty('--card', `${backgroundHsl.h} ${backgroundHsl.s}% ${Math.min(100, backgroundHsl.l + 2)}%`);
    root.style.setProperty('--popover', `${backgroundHsl.h} ${backgroundHsl.s}% ${Math.min(100, backgroundHsl.l + 2)}%`);
  }
  if (foregroundHsl) {
    root.style.setProperty('--foreground', `${foregroundHsl.h} ${foregroundHsl.s}% ${foregroundHsl.l}%`);
    root.style.setProperty('--card-foreground', `${foregroundHsl.h} ${foregroundHsl.s}% ${foregroundHsl.l}%`);
    root.style.setProperty('--popover-foreground', `${foregroundHsl.h} ${foregroundHsl.s}% ${foregroundHsl.l}%`);
  }

  cacheTheme(normalized);
}
